import store from '@/store';
import {UAParser} from 'ua-parser-js';
import {postUserLog} from '@/api/log';
import SentryHelper from '@/js/common/sentry';
import {getMediaPermissionsStatus} from '@/js/utils/camera';
import {
  PERMISSION_STATE,
  USER_LOG_STATUS,
  USER_LOG_TYPE,
} from '@/js/common/constants';

// NetWork
const getNetwork = () => {
  if (!navigator?.connection) {
    return {
      effectiveType: 'not support',
      downLink: 'not support',
      rtt: 'not support',
    };
  }

  const {effectiveType, downlink, rtt} = navigator.connection;

  return {
    effectiveType: effectiveType ?? 'not support',
    downLink: downlink ? `${downlink}Mbps` : 'not support',
    rtt: rtt ?? 'not support',
  };
};

// Performance
const getPerformance = () => {
  const performance = {
    memory: navigator?.deviceMemory ?? 'not support',
    core: navigator?.hardwareConcurrency ?? 'not support',
  };

  return performance;
};

// UserAgent
const getAgent = () => {
  try {
    const uap = new UAParser();
    const userAgent = uap.getResult();

    return userAgent;
  } catch (error) {
    return {};
  }
};

export const updateLogStreamsAndDevice = async stream => {
  if (!stream) {
    store.commit('user/setUserInitFlags', {
      stream: true,
      device: true,
      loadFRModel: true,
    });
    return;
  }

  getDevice(stream);
  getMediapipeStreams(stream);
};

// device
const getDevice = async stream => {
  const getList = async () => {
    const devices = await navigator.mediaDevices?.enumerateDevices();

    if (devices.length <= 0) {
      return [];
    }
    return devices;
  };

  const getCurrent = (deviceList, stream) => {
    const rawTracks = stream.getTracks();

    const currentDevice = deviceList.filter(device =>
      rawTracks.some(track => device.label === track.label),
    );

    if (currentDevice.length <= 0) return;

    let current = {};
    currentDevice.map(device => {
      const {kind, deviceId, label} = device;

      if (kind === 'videoinput') {
        current.camera = {id: deviceId, label};
      } else if (kind === 'audioinput') {
        current.audio = {id: deviceId, label};
      }
    });

    return current;
  };

  let result = {};
  try {
    const list = await getList();
    result = {
      list,
    };

    const current = getCurrent(list, stream);
    result = {...result, current: current};
  } catch (error) {
    sentryError('handleDevice', error);
  } finally {
    // 실패했을때도 이전까지의 성공 데이터를 저장
    store.commit('user/setUserLogs', {device: result});
    store.commit('user/setUserInitFlags', {device: true});
  }
};

// streams
const getMediapipeStreams = async stream => {
  const getTrack = () => {
    const rawTracks = stream.getTracks();
    const tracks = rawTracks.map(track => {
      const capability = track.getCapabilities();
      const setting = track.getSettings();
      const constraint = track.getConstraints();

      return {
        id: track.id,
        enabled: track.enabled,
        label: track.label,
        readyState: track.readyState,
        muted: track.muted,
        // Q. kind값에 맞춰서 데이터 분리할까?
        kind: track.kind, // 비디오인지 오디오인지
        stats: track.stats, // 비디오와 오디오가 각각 다른 데이터를 리턴
        capability: capability, // 비디오와 오디오가 각각 다른 데이터를 리턴
        settings: setting, // 비디오와 오디오가 각각 다른 데이터를 리턴
        constraint: constraint, // 연결 조건
      };
    });

    return tracks;
  };

  let result = {
    id: stream.id,
    active: stream.active, // 스트림 활성화 상태
  };

  try {
    const tracks = await getTrack();
    result = {...result, ...{tracks: tracks}};
  } catch (error) {
    sentryError('getMediapipeStreams', error);
  } finally {
    store.commit('user/setUserLogs', {streams: result});
    store.commit('user/setUserInitFlags', {stream: true});
  }
};

// LoadFRModel
export const updateLogLoadFRModel = status => {
  store.commit('user/setUserLogs', {loadFRModel: status});
  store.commit('user/setUserInitFlags', {loadFRModel: true});
};

// Permission : "prompt"상태 유지하는 케이스로 인해 n초 후 권한 체크
// n: 기기연결체크 5초, 시험장 30초
export const updateLogPermission = async (userId, useMicrophone, logType) => {
  const {camera, microphone} = await getMediaPermissionsStatus();

  setTimeout(
    () => {
      try {
        const hasCameraPermission = camera?.state === PERMISSION_STATE.GRANTED;
        const hasMicrophonePermission =
          microphone?.state === PERMISSION_STATE.GRANTED;

        const pass =
          hasCameraPermission && (!useMicrophone || hasMicrophonePermission);

        if ((!camera && !microphone) || !pass) {
          throw new Error('This is not a normal permission.');
        }

        store.commit('user/setUserLogs', {
          permission: {
            video: camera.state,
            audio: microphone.state,
          },
        });

        // 권한 미선택("prompt")유지시
        // 로직들이 멈춰버리므로 함수 scope내 api호출 필요
        // permission true 변경시 api 중복 호출되므로 에러시에는 값을 변경하지 않는다.
        store.commit('user/setUserInitFlags', {permission: true});
      } catch (error) {
        store.commit('user/setUserLogs', {
          permission: {
            video: camera?.state ?? 'not support',
            audio: microphone?.state ?? 'not support',
          },
        });
        const params = {
          userId,
          logType: logType,
          status: USER_LOG_STATUS.INIT,
        };
        sendLog(params);
      }
    },
    logType === USER_LOG_TYPE.FRONT_FINAL ? 1000 * 30 : 1000 * 5,
  );

  const getPermissionLog = (type, state) => {
    const log = store.getters['user/getUserLogs'];

    if (!log) return;

    store.commit('user/setUserLogs', {
      ...log,
      permission: {
        ...log.permission,
        [type]: state,
      },
    });
  };

  camera.onchange = () => getPermissionLog('video', camera.state);
  microphone.onchange = () => getPermissionLog('audio', microphone.state);
};

// Navigator
export const updateLogNavigator = async () => {
  let result = {};
  try {
    const network = getNetwork();
    const performance = getPerformance();
    const userAgent = getAgent();

    result = {
      network,
      performance,
      userAgent,
    };
  } catch (error) {
    sentryError('updateLogNavigator', error);
  } finally {
    // 실패했을때도 이전까지의 성공 데이터를 저장
    store.commit('user/setUserLogs', result);
    store.commit('user/setUserInitFlags', {navigator: true});
  }
};

// Mobile쪽 FRModel, Stream, Device는 Logs로 보내지 않아서 상태 변경
export const updateLogMobile = () => {
  store.commit('user/setUserInitFlags', {
    loadFRModel: true,
    stream: true,
    device: true,
  });
};

const sentryError = (type, error) => {
  const {id: userId} = store.getters['user/getUser'];

  SentryHelper.captureMessage({
    error,
    userId,
    messageTitle: `[Logs ${type}] callback error: ${error}`,
  });
};

// API 호출
export const sendLog = async params => {
  try {
    const logs = store.getters['user/getUserLogs'];
    await postUserLog({...params, ...{log: logs}});
  } catch (error) {
    sentryError('sendLog', error);
  }
};
